import { apiUrl } from './apiConfig';

export interface User {
    username: string;
    clientId: string;
    email: string;
    displayName: string;
    isAdmin?: boolean;
    allowedDocTypes?: string;
    allowedPartners?: string;
    portalAccess?: {
        features?: Record<string, boolean>;
        allowedDocTypes?: string;
        allowedPartners?: string;
        documentTypes?: string[] | null;
        partners?: string[] | null;
    };
}

export interface LoginResponse {
    success: boolean;
    token: string;
    user: User;
}

export interface FileInfo {
    name: string;
    path: string;
    size: number;
    lastModified: string;
    contentType: string;
}

export interface PartnerInfo {
    partnerId: string;
    partnerName: string;
    supportedDocTypes: string[];
    status: 'active' | 'onboarding' | 'inactive' | 'planned';
}

export interface AccessCatalogResponse {
    partners: string[];
    documentTypes: string[];
}

export interface UploadResponse {
    success: boolean;
    message: string;
    file: {
        originalName: string;
        storedPath: string;
        size: number;
        uploadedAt: string;
    };
}

function getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('portal_token');
    if (token) {
        return { Authorization: `Bearer ${token}` };
    }
    return {};
}

export async function login(username: string, password: string): Promise<LoginResponse> {
    const response = await fetch(apiUrl('auth', '/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();

    // Store token in localStorage for API calls
    if (data.token) {
        localStorage.setItem('portal_token', data.token);
    }

    return data;
}

export async function getCurrentUser(): Promise<User | null> {
    try {
        const response = await fetch(apiUrl('auth', '/auth/me'), {
            headers: getAuthHeaders(),
            credentials: 'include',
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json();
        return data.user;
    } catch {
        return null;
    }
}

export async function uploadFile(file: File, clientId?: string, docType?: string, partnerId?: string): Promise<UploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    if (docType) formData.append('docType', docType);
    if (partnerId) formData.append('partnerId', partnerId);

    const url = clientId
        ? `${apiUrl('files', '/files/upload')}?clientId=${encodeURIComponent(clientId)}`
        : apiUrl('files', '/files/upload');

    const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
    }

    return response.json();
}

export async function listFiles(folder?: string, clientId?: string): Promise<FileInfo[]> {
    const params = new URLSearchParams();
    if (folder) params.set('folder', folder);
    if (clientId) params.set('clientId', clientId);

    const queryString = params.toString();
    const url = queryString ? `${apiUrl('files', '/files')}?${queryString}` : apiUrl('files', '/files');

    const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to list files');
    }

    const data = await response.json();
    // Map backend field names to frontend FileInfo interface
    return (data.files || []).map((f: any) => ({
        name: f.fileName || f.name || '',
        path: f.fileId || f.path || '',
        size: f.fileSize ?? f.size ?? 0,
        lastModified: f.uploadedAt || f.lastModified || '',
        contentType: f.contentType || '',
    }));
}

export interface AuditEntry {
    timestamp: string;
    action: string;
    details: string;
    username: string;
}

export async function listAuditLogs(limit?: number, clientId?: string, actions?: string[]): Promise<AuditEntry[]> {
    const params = new URLSearchParams();
    if (limit) params.set('limit', limit.toString());
    if (clientId) params.set('clientId', clientId);
    if (actions && actions.length > 0) params.set('actions', actions.join(','));

    const queryString = params.toString();
    const url = queryString ? `${apiUrl('mgmt', '/audit')}?${queryString}` : apiUrl('mgmt', '/audit');

    const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to list audit logs');
    }

    const data = await response.json();
    return data.entries;
}

export async function listPartners(clientId?: string): Promise<PartnerInfo[]> {
    const headers: HeadersInit = {
        ...getAuthHeaders(),
        ...(clientId ? { 'x-active-client-id': clientId } : {}),
    };

    const response = await fetch(apiUrl('mgmt', '/partners'), {
        headers,
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to list partners');
    }

    const data = await response.json();
    return data.partners || [];
}

export async function getAccessCatalog(clientId?: string): Promise<AccessCatalogResponse> {
    const params = new URLSearchParams();
    if (clientId) params.set('clientId', clientId);

    const queryString = params.toString();
    const url = queryString ? `${apiUrl('mgmt', '/management/access-catalog')}?${queryString}` : apiUrl('mgmt', '/management/access-catalog');

    const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to load access catalog');
    }

    return response.json();
}

export interface DownloadResponse {
    success: boolean;
    downloadUrl: string;
    expiresAt: string;
    fileName: string;
}

export async function getDownloadUrl(filePath: string): Promise<DownloadResponse> {
    const response = await fetch(`${apiUrl('files', '/files/download')}?path=${encodeURIComponent(filePath)}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get download link');
    }

    return response.json();
}

// Password Management APIs

export interface VerifyTokenResponse {
    valid: boolean;
    email?: string;
    username?: string;
    expiresAt?: string;
    error?: string;
}

export async function verifyToken(token: string, type: 'invite' | 'reset'): Promise<VerifyTokenResponse> {
    const response = await fetch(`${apiUrl('auth', '/auth/verify-token')}?token=${encodeURIComponent(token)}&type=${type}`);

    if (!response.ok) {
        const error = await response.json();
        return { valid: false, error: error.error || 'Invalid token' };
    }

    return response.json();
}

export interface SetPasswordResponse {
    success: boolean;
    message: string;
    username?: string;
    error?: string;
}

export async function setPassword(token: string, type: 'invite' | 'reset', password: string): Promise<SetPasswordResponse> {
    const response = await fetch(apiUrl('auth', '/auth/set-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, type, password }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to set password');
    }

    return data;
}

export interface ForgotPasswordResponse {
    success: boolean;
    message: string;
    _dev?: {
        resetUrl: string;
        resetToken: string;
        note: string;
    };
}

export async function requestPasswordReset(email: string): Promise<ForgotPasswordResponse> {
    const response = await fetch(apiUrl('auth', '/auth/forgot-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to request password reset');
    }

    return data;
}

// Admin User Management APIs

export interface AdminUser {
    username: string;
    clientId: string;
    email: string;
    displayName: string;
    isActive: boolean;
    pendingInvite: boolean;
    createdAt: string;
    lastLogin?: string;
    portalFeatureOverridesJson?: string;
    allowedPartnersOverrideJson?: string;
    allowedDocTypesOverrideJson?: string;
}

export interface ListUsersResponse {
    success: boolean;
    users: AdminUser[];
}

export async function listAllUsers(): Promise<AdminUser[]> {
    const response = await fetch(apiUrl('mgmt', '/management/users'), {
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to list users');
    }

    const data = await response.json();
    return data.users;
}

export interface CreateUserRequest {
    username: string;
    clientId: string;
    email: string;
    displayName: string;
}

export interface CreateUserResponse {
    success: boolean;
    user: AdminUser;
    inviteUrl: string;
    emailSent: boolean;
    message?: string;
}

export async function createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
    const response = await fetch(apiUrl('mgmt', '/management/users'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
        },
        body: JSON.stringify(userData),
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to create user');
    }

    return data;
}

export async function deleteUser(username: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(apiUrl('mgmt', `/management/users/${encodeURIComponent(username)}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to delete user');
    }

    return data;
}

export interface UpdateUserRequest {
    email?: string;
    displayName?: string;
    clientId?: string;
    isActive?: boolean;
}

export interface UpdateUserResponse {
    success: boolean;
    message: string;
    user: AdminUser;
}

export async function updateUser(username: string, updates: UpdateUserRequest): Promise<UpdateUserResponse> {
    const response = await fetch(apiUrl('mgmt', `/management/users/${encodeURIComponent(username)}`), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
        },
        body: JSON.stringify(updates),
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to update user');
    }

    return data;
}

export async function resendInvite(username: string, email: string): Promise<CreateUserResponse> {
    // Re-create the invite by calling the create endpoint with the same data
    // The backend will handle refreshing the token
    const response = await fetch(apiUrl('mgmt', `/management/resend-invite/${encodeURIComponent(username)}`), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
        },
        body: JSON.stringify({ email }),
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to resend invite');
    }

    return data;
}

// ============ Client Management ============

export interface ClientInfo {
    clientId: string;
    displayName: string;
    sftpFolder: string;
    isActive: boolean;
    allowedDocTypes: string; // Comma-separated (e.g., '810,850')
    allowedPartners: string; // Comma-separated (e.g., 'ANDA,WALGREENS')
    allowedDocTypesJson?: string;
    allowedPartnersJson?: string;
    portalFeaturesJson?: string;
    createdAt: string;
    updatedAt?: string;
}

export async function listClients(): Promise<ClientInfo[]> {
    const response = await fetch(apiUrl('mgmt', '/management/clients'), {
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to list clients');
    }

    const data = await response.json();
    return data.clients;
}

export async function createClient(clientData: {
    clientId: string;
    displayName: string;
    sftpFolder: string;
    allowedDocTypes?: string;
    allowedPartners?: string;
    allowedDocTypesJson?: string;
    allowedPartnersJson?: string;
}): Promise<{ success: boolean; client: ClientInfo }> {
    const response = await fetch(apiUrl('mgmt', '/management/clients'), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
        },
        body: JSON.stringify(clientData),
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to create client');
    }

    return data;
}

export async function updateClient(clientId: string, updates: {
    displayName?: string;
    sftpFolder?: string;
    isActive?: boolean;
    allowedDocTypes?: string;
    allowedPartners?: string;
    allowedDocTypesJson?: string;
    allowedPartnersJson?: string;
}): Promise<{ success: boolean; message: string }> {
    const response = await fetch(apiUrl('mgmt', `/management/clients/${encodeURIComponent(clientId)}`), {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
        },
        body: JSON.stringify(updates),
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to update client');
    }

    return data;
}

export async function deleteClient(clientId: string): Promise<{ success: boolean; message: string }> {
    const response = await fetch(apiUrl('mgmt', `/management/clients/${encodeURIComponent(clientId)}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Failed to delete client');
    }

    return data;
}
